import { useMutation, useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { getUserID } from '../../../utils'
import {
    createWorkspace,
    deleteWorkspace,
    getUserWorkspaces,
    renameWorkspace,
    submitRequest,
} from './workspaceApi'
import { useUserInfo } from '../user/useUserInfo'
import { EVENTS, trackEvent } from '../../../events'

export const useWorkspaces = () => {
    const userID = getUserID()
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated)

    const { user } = useUserInfo()

    const {
        data: workspaces,
        refetch: getWorkspaces,
        isLoading: isLoadingWorkspaces,
    } = useQuery({
        queryKey: ['workspaces', userID],
        queryFn: () => getUserWorkspaces(userID),
        enabled: !!userID && isAuthenticated,
    })

    const { mutate: create, isPending: isCreating } = useMutation({
        mutationFn: ({ workspaceName }) => createWorkspace(workspaceName, user.userName),
        onSuccess: (response, variables) => {
            trackEvent(EVENTS.WORKSPACE.CREATE, {
                workspace: {
                    name: variables.workspaceName,
                    workspaceID: response.workspaceID,
                },
            })
            getWorkspaces().then((w) => {
                variables.onSuccess(response)
            })
        },
    })

    const { mutate: rename, isPending: isRenaming } = useMutation({
        mutationFn: ({ workspaceName, workspaceID }) => renameWorkspace(workspaceName, workspaceID),
        onSuccess: (response, variables) => {
            getWorkspaces().then((w) => {
                variables.onSuccess(w)
            })
        },
    })

    const { mutate: remove, isPending: isRemoving } = useMutation({
        mutationFn: ({ workspaceID }) => deleteWorkspace(workspaceID),
        onSuccess: (response, variables) => {
            getWorkspaces().then((w) => {
                variables.onSuccess(w.data)
            })
        },
    })

    const { mutate: submit, isPending: isSubmittingRequest } = useMutation({
        mutationFn: ({ email, message }) => submitRequest(email, message),
        onSuccess: (response, variables) => {
            variables?.onSuccess()
        },
    })

    return {
        workspaces: workspaces || [],
        getWorkspaces,
        isLoadingWorkspaces,
        create,
        isCreating,
        rename,
        isRenaming,
        remove,
        isRemoving,
        submit,
        isSubmittingRequest,
    }
}
